import { useCallback, useEffect, useState } from 'react';
import { describeError, listVersions } from '../../lib/payPolicyTemplates/api';
import { PayPolicyTemplate } from './types';

interface Answer {
  uuid: string;
  versions: PayPolicyTemplate[] | null;
  error: string | null;
}

/** The family's versions, highest first, asked for once per uuid (and again on retry); null skips the call. */
export const useTemplateVersions = (uuid: string | null) => {
  // Kept with the uuid it answers: a list for another uuid never applies, not even until this one's lands.
  const [answer, setAnswer] = useState<Answer | null>(null);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (uuid === null) return undefined;
    let cancelled = false;
    setAnswer(null);
    listVersions(uuid)
      .then((found) => {
        if (!cancelled) setAnswer({ uuid, versions: [...found].sort((a, b) => b.version - a.version), error: null });
      })
      .catch((caught) => {
        if (!cancelled) setAnswer({ uuid, versions: null, error: describeError(caught) });
      });
    return () => {
      cancelled = true;
    };
  }, [uuid, attempt]);

  const retry = useCallback(() => setAttempt((current) => current + 1), []);
  const current = uuid !== null && answer?.uuid === uuid ? answer : null;
  const versions = current?.versions ?? null;
  return { versions, newest: versions?.[0] ?? null, loading: uuid !== null && current === null, error: current?.error ?? null, retry };
};
